/*
 * Appeals Reducer
 *
 * The reducer takes care of our data. Using actions, we can change our
 * application state.
 * To add a new action, add it to the switch statement in the reducer function
 *
 */

import { ServiceCategoriesDropdown } from 'containers/AppealResponses/modules/types';
import {
  ActionTypesAppeal,
  APPEALS_LOOKUP_SEARCH_TYPE,
  AppealsDetailPaneInititalState,
} from './constants';
import {
  AppealRecommendationsDropdown,
  AppealResponsesDropdown,
  AppealSearchRequest,
  AppealsHeaderData,
  AppealsInitialState,
  ReconsiderationGridDataResponse,
} from './types';
import { AppealWorkQueueTypes } from './enum';

// The initial state of the App
const initialState: AppealsInitialState = {
  // navigation
  appealsSearchId: '',
  appealsSearchType: APPEALS_LOOKUP_SEARCH_TYPE.APPEAL_ID,
  appealsGridData: [],
  appealsAdvancedSearchParams: {} as AppealSearchRequest,
  appealsWorkQueueTitle: AppealWorkQueueTypes.all,
  appealsWorkQueueGridData: [],
  isAppealsWorkQueueGridLoading: false,
  isAppealsNavQueueLoading: false,
  showAppealsSearchGrid: false,
  appealsWorkQueueCpIDs: '',
  appealsWorkQueueServiceIDs: '',
  appealsQueueSelectedIds: new Set(),
  appealsQueueOpenGroupIds: new Set(),
  isAppealsWorkQueueGridFiltered: false,
  clientsByStatus: [],
  servicesByStatus: [],
  isAppealsLoading: false,
  isAppealsHeaderLoading: false,
  appealsHeaderData: {} as AppealsHeaderData,
  reconsiderationData: [] as ReconsiderationGridDataResponse[],
  reconsiderationDropdowns: {
    appealRecommendations: [],
    appealResponses: [],
  } as {
    appealRecommendations: AppealRecommendationsDropdown[];
    appealResponses: AppealResponsesDropdown[];
  },
  isReconsiderationDropdownsLoading: false,
  appealNotesData: [],
  isReconsiderationLoading: false,
  isAppealsLookupLoading: false,
  appealsAdvancedSearchLookupData: null,
  clientDivisionDropDownData: [],
  isAppealsAttachmentInfoLaoding: false,
  appealsAttachmentInfoData: [],
  isDetailPaneLoading: false,
  isAppealNotesLoading: false,
  appealDocumentTypeData: [],
  isAppealDocumentTypeLoading: false,
  isUpdateAppealDocTypeLoading: false,
  allAppealReconsiderations: {
    appealRecommendations: [],
    appealResponses: [],
    serviceCategories: [],
  } as {
    appealRecommendations: AppealRecommendationsDropdown[];
    appealResponses: AppealResponsesDropdown[];
    serviceCategories: ServiceCategoriesDropdown[];
  },
  isAllAppealReconsiderationsLoading: false,
  isExportAppealAdvancedSearchLoading: false,
  isUpdateAssigneeLoading: false,
  isAddAssigneeLoading: false,
  isAppealActivityInfoLoading: false,
  appealActivityInfoData: [],
  appealGridColumnConfig: [],
};

function appealsReducer(state = initialState, action) {
  switch (action.type) {
    case ActionTypesAppeal.SET_APPEALS_SEARCH_ID:
      return {
        ...state,
        appealsSearchId: action.searchId,
      };
    case ActionTypesAppeal.SET_APPEALS_SEARCH_TYPE:
      return {
        ...state,
        appealsSearchType: action.appealsSearchType,
      };
    case ActionTypesAppeal.SET_APPEALS_SEARCH_GRID:
      return {
        ...state,
        appealsGridData: action.gridData,
      };
    case ActionTypesAppeal.SET_APPEALS_ADVANCED_SEARCH_PARAMS:
      return {
        ...state,
        appealsAdvancedSearchParams: action.appealsAdvancedSearchParams,
      };
    case ActionTypesAppeal.SET_APPEALS_WORK_QUEUE_TITLE:
      return {
        ...state,
        appealsWorkQueueTitle: action.appealsWorkQueueTitle,
      };
    case ActionTypesAppeal.SET_APPEALS_WORK_QUEUE_CPIDS:
      return {
        ...state,
        appealsWorkQueueCpIDs: action.appealsWorkQueueCpIDs,
      };
    case ActionTypesAppeal.SET_APPEALS_WORK_QUEUE_SERVICE_IDS:
      return {
        ...state,
        appealsWorkQueueServiceIDs: action.appealsWorkQueueServiceIDs,
      };
    case ActionTypesAppeal.TOGGLE_APPEALS_NAV_QUEUE_LOADING:
      return {
        ...state,
        isAppealsNavQueueLoading: action.isLoading,
      };
    case ActionTypesAppeal.SET_APPEALS_WORK_QUEUE_GRID_FILTER:
      return {
        ...state,
        isAppealsWorkQueueGridFiltered: action.isAppealsWorkQueueGridFiltered,
      };
    case ActionTypesAppeal.TOGGLE_APPEALS_WORK_QUEUE_GRID_LOADING:
      return {
        ...state,
        isAppealsWorkQueueGridLoading: action.isLoading,
      };
    case ActionTypesAppeal.SET_APPEALS_WORK_QUEUE_GRID:
      return {
        ...state,
        appealsWorkQueueGridData: action.gridData,
      };

    case ActionTypesAppeal.TOGGLE_SHOW_APPEALS_SEARCH_GRID:
      return {
        ...state,
        showAppealsSearchGrid: action.showSearchGrid,
      };
    case ActionTypesAppeal.SET_APPEAL_GRID_COLUMN_CONFIG:
      return {
        ...state,
        appealGridColumnConfig: action.payload,
      };
    case ActionTypesAppeal.TOGGLE_APPEALS_LOADING:
      return {
        ...state,
        isAppealsLoading: action.isAppealsLoading,
      };
    case ActionTypesAppeal.TOGGLE_APPEALS_HEADER_LOADING:
      return {
        ...state,
        isAppealsHeaderLoading: action.isAppealsHeaderLoading,
      };
    case ActionTypesAppeal.SET_APPEALS_HEADER_DATA:
      return {
        ...state,
        appealsHeaderData: action.headerData,
      };
    case ActionTypesAppeal.SET_RECONSIDERATION_DATA:
      return {
        ...state,
        reconsiderationData: action.reconsiderationData,
      };
    case ActionTypesAppeal.SET_RECONSIDERATION_DROPDOWNS:
      return {
        ...state,
        reconsiderationDropdowns: action.reconsiderationDropdowns,
      };
    case ActionTypesAppeal.SET_APPEAL_NOTES_DATA:
      return {
        ...state,
        appealNotesData: action.appealNotesData,
      };
    case ActionTypesAppeal.TOGGLE_RECONSIDERATION_LOADING:
      return {
        ...state,
        isReconsiderationLoading: action.isReconsiderationLoading,
      };
    case ActionTypesAppeal.SET_APPEALS_ADVANCED_SEARCH_LOOPKUP_DATA:
      return {
        ...state,
        appealsAdvancedSearchLookupData: action.lookupData,
      };
    case ActionTypesAppeal.TOGGLE_APPEALS_ADVANCED_SEARCH_LOOPKUP_LOADING:
      return {
        ...state,
        isAppealsLookupLoading: action.isLoading,
      };

    case ActionTypesAppeal.SET_APPEALS_ADVANCED_SEARCH_CLIENT_DIVISION_DROPDOWN_DATA:
      return {
        ...state,
        clientDivisionDropDownData: action.divisionsForSelectedClient,
      };
    case ActionTypesAppeal.SET_APPEAL_ACTIVITY_INFO_DATA:
      return {
        ...state,
        appealActivityInfoData: action.data,
      };
    case ActionTypesAppeal.TOGGLE_APPEAL_ACTIVITY_INFO_LOADING:
      return {
        ...state,
        isAppealActivityInfoLoading: action.isLoading,
      };
    case ActionTypesAppeal.TOGGLE_APPEAL_ATTACHMENTS_LOADING:
      return {
        ...state,
        isAppealsAttachmentInfoLaoding: action.isLoading,
      };

    case ActionTypesAppeal.SET_APPEALS_ATTACHMENT_INFO_DATA:
      return {
        ...state,
        appealsAttachmentInfoData: action.data,
      };
    case ActionTypesAppeal.SET_APPEAL_DOCUMENT_TYPE_DATA:
      return {
        ...state,
        appealDocumentTypeData: action.appealDocumentTypeData,
      };
    case ActionTypesAppeal.SET_ALL_APPEAL_RECONSIDERATIONS:
      return {
        ...state,
        allAppealReconsiderations: action.data,
      };
    case ActionTypesAppeal.CLEAR_APPEALS_DETAIL_PANE_REDUX_DATA:
      return {
        ...state,
        ...AppealsDetailPaneInititalState,
        appealGridColumnConfig: state.appealGridColumnConfig,
        appealsSearchId: state.appealsSearchId,
        appealsSearchType: state.appealsSearchType,
        appealsAdvancedSearchParams: state.appealsAdvancedSearchParams,
        appealsWorkQueueTitle: state.appealsWorkQueueTitle,
        appealsWorkQueueGridData: state.appealsWorkQueueGridData,
        appealsQueueOpenGroupIds: state.appealsQueueOpenGroupIds,
        appealsQueueSelectedIds: state.appealsQueueSelectedIds,
        clientsByStatus: state.clientsByStatus,
        appealsWorkQueueCpIDs: state.appealsWorkQueueCpIDs,
        appealsWorkQueueServiceIDs: state.appealsWorkQueueServiceIDs,
        isAppealsWorkQueueGridFiltered: state.isAppealsWorkQueueGridFiltered,
      };
    case ActionTypesAppeal.CLEAR_APPEALS_SEARCH_PANE_REDUX_DATA:
      return {
        ...state,
        appealsSearchId: '',
        appealsSearchType: state.appealsSearchType,
        appealsGridData: [],
        appealsAdvancedSearchParams: {} as AppealSearchRequest,
        showAppealsSearchGrid: false,
      };
    case ActionTypesAppeal.TOGGLE_IS_ALL_APPEAL_RECONSIDERATION_LOADING:
      return {
        ...state,
        isAllAppealReconsiderationsLoading: action.isLoading,
      };
    case ActionTypesAppeal.TOGGLE_RECONSIDERATION_DROPDOWNS_LOADING:
      return {
        ...state,
        isReconsiderationDropdownsLoading: action.isReconsiderationDropdownsLoading,
      };
    case ActionTypesAppeal.TOGGLE_DETAIL_PANE_LOADING:
      return {
        ...state,
        isDetailPaneLoading: action.isDetailPaneLoading,
      };
    case ActionTypesAppeal.TOGGLE_APPEAL_NOTES_LOADING:
      return {
        ...state,
        isAppealNotesLoading: action.isAppealNotesLoading,
      };
    case ActionTypesAppeal.TOGGLE_APPEAL_DOCUMENT_TYPE_LOADING:
      return {
        ...state,
        isAppealDocumentTypeLoading: action.isAppealDocumentTypeLoading,
      };
    case ActionTypesAppeal.TOGGLE_UPDATE_APPEAL_DOC_TYPE_LOADING:
      return {
        ...state,
        isUpdateAppealDocTypeLoading: action.isUpdateAppealDocTypeLoading,
      };
    case ActionTypesAppeal.TOGGLE_EXPORT_APPEAL_ADVANCED_SEARCH_LOADING:
      return {
        ...state,
        isExportAppealAdvancedSearchLoading: action.isLoading,
      };
    case ActionTypesAppeal.TOGGLE_IS_UPDATE_ASSIGNEE_LOADING:
      return {
        ...state,
        isUpdateAssigneeLoading: action.isLoading,
      };
    case ActionTypesAppeal.TOGGLE_IS_ADD_ASSIGNEE_LOADING:
      return {
        ...state,
        isAddAssigneeLoading: action.isLoading,
      };
    case ActionTypesAppeal.SET_CLIENT_QUEUE_SELECTED_IDS:
      return {
        ...state,
        appealsQueueSelectedIds: action.appealsQueueSelectedIds,
      };
    case ActionTypesAppeal.SET_CLIENT_QUEUE_OPEN_GROUP_IDS:
      return {
        ...state,
        appealsQueueOpenGroupIds: action.appealsQueueOpenGroupIds,
      };
    case ActionTypesAppeal.SET_APPEALS_CLIENTS_BY_STATUS:
      return {
        ...state,
        clientsByStatus: action.clientsByStatus,
      };
    case ActionTypesAppeal.SET_APPEALS_SERVICES_BY_STATUS:
      return {
        ...state,
        servicesByStatus: action.servicesByStatus,
      };
    default:
      return state;
  }
}

export default appealsReducer;
